<template>
    <div class="component-container">
        <div v-if="error" class="error-popup-overlay">
            <div class="error-popup">
            <p>{{ errorMessage }}</p>
            <button @click="clearError">Schließen</button>
            </div>
        </div>
        <div class="title-container flex-top">
            <div class="logo-container">
            <img src="https://sander-gruppe.eu/wp-content/uploads/2019/07/ms-logo.png" alt="Ihr Logo" class="logo">
            </div>
            <div class="component-container-header">
                Konfigurator
            </div>
        </div>
        <div class="component-container-text" :class="{ 'has-admin-online-summary': isInternalAdmin }">
            <div class="flex-top">
                <div>Willkommen in Ihrem Kundenkonto. Hier können Sie neue Auftäge konfigurieren, Projekte einsehen und Ihre Stammdaten und Information direkt verwalten.</div>
                <div class="your-contact">
                    <div class="header">Ihr Ansprechpartner</div>
                    <div class="contact-wrapper">
                        <div class="contact-right">
                            <img :src="contactImageUrl" alt="Icon">
                        </div>
                        <div class="contact-left">
                            <div class="contact-name">
                                <div class="content bold-bigger">{{ contact != null ? contact.name : "" }}</div>
                                <div class="content">{{ contact != null ? contact.position : "" }}</div>
                                <div class="content">{{ contact != null ? contact.bereich : "" }}</div>
                            </div>
                            <div class="contact-contact">
                            <a
                                v-if="contact && contact.telefon"
                                class="content"
                                :href="formatTel(contact.telefon)"
                            >
                                {{ contact.telefon }}
                            </a>
                            <div v-else class="content"></div>
                            <a
                                v-if="contact && contact.mobil"
                                class="content"
                                :href="formatTel(contact.mobil)"
                            >
                                {{ contact.mobil }}
                            </a>
                            <div v-else class="content"></div>
                            <a
                                v-if="contact && contact.email"
                                class="content"
                                :href="'mailto:' + contact.email"
                            >
                                {{ contact.email }}
                            </a>
                            <div v-else class="content"></div>
                            </div>
                        </div>
                    </div>
                    <!-- Fügen Sie weitere Felder entsprechend dem Bild hinzu -->
                </div>
            </div>
        </div>
        <div v-if="isInternalAdmin" class="admin-online-summary-row">
            <div class="online-summary" aria-live="polite">
                <div class="online-summary-item">
                    <span class="online-dot"></span>
                    <span>Online</span>
                    <strong>{{ activitySummary.online_total }}</strong>
                </div>
                <div class="online-summary-item">
                    <span class="online-dot online-dot-ms"></span>
                    <span>MS-Nutzer</span>
                    <strong>{{ activitySummary.ms_users_online }}</strong>
                </div>
                <div class="online-summary-item">
                    <span class="online-dot online-dot-customer"></span>
                    <span>Kunden</span>
                    <strong>{{ activitySummary.customer_users_online }}</strong>
                </div>
            </div>
        </div>
        <section v-if="isInternalAdmin" class="kpi-dashboard" :class="{ 'is-expanded': kpiDashboardExpanded }">
            <div class="kpi-dashboard-heading">
                <h2>KPI Dashboard — Live-Übersicht</h2>
                <button
                    type="button"
                    class="kpi-dashboard-toggle"
                    :aria-expanded="kpiDashboardExpanded"
                    :aria-label="kpiDashboardExpanded ? 'KPI Dashboard einklappen' : 'KPI Dashboard ausklappen'"
                    aria-controls="kpi-dashboard-content"
                    @click="kpiDashboardExpanded = !kpiDashboardExpanded"
                >
                    <span class="kpi-dashboard-chevron" :class="{ 'is-expanded': kpiDashboardExpanded }" aria-hidden="true"></span>
                </button>
            </div>
            <div v-show="kpiDashboardExpanded" id="kpi-dashboard-content" class="kpi-dashboard-content">
                <article class="kpi-card">
                    <h3>Nutzung</h3>
                    <div class="kpi-card-body">
                        <div class="kpi-metric">
                            <span class="kpi-label">Anzahl Nutzer gesamt</span>
                            <strong>{{ formatKpiInteger(activitySummary.users_total) }}</strong>
                        </div>
                        <div class="kpi-metric">
                            <span class="kpi-label">Anzahl Nutzer aktiv</span>
                            <strong>{{ formatKpiInteger(activitySummary.active_users_total) }}</strong>
                        </div>
                        <div class="kpi-online-list">
                            <span class="kpi-label">Aktuell online</span>
                            <div class="kpi-online-users-scroll">
                                <div v-for="onlineUser in activitySummary.online_users" :key="onlineUser.id" class="kpi-online-user">
                                    <span class="kpi-status-dot"></span>{{ onlineUserLabel(onlineUser) }}
                                </div>
                                <div v-if="!activitySummary.online_users.length" class="kpi-online-empty">
                                    Keine Nutzer online
                                </div>
                            </div>
                        </div>
                    </div>
                </article>
                <article class="kpi-card">
                    <h3>Bestellungen</h3>
                    <div class="kpi-card-body">
                        <span class="kpi-label">Anzahl Bestellungen</span>
                        <div class="kpi-period-grid">
                            <div><span>Heute</span><strong>{{ formatOrderCount(activitySummary.orders.today) }}</strong></div>
                            <div><span>Woche</span><strong>{{ formatOrderCount(activitySummary.orders.week) }}</strong></div>
                            <div><span>Monat</span><strong>{{ formatOrderCount(activitySummary.orders.month) }}</strong></div>
                        </div>
                        <span class="kpi-label kpi-section-label">Summe Bestellwert</span>
                        <div class="kpi-period-grid">
                            <div><span>Heute</span><strong>{{ formatKpiCurrency(activitySummary.orders.today.gross_total) }}</strong></div>
                            <div><span>Woche</span><strong>{{ formatKpiCurrency(activitySummary.orders.week.gross_total) }}</strong></div>
                            <div><span>Monat</span><strong>{{ formatKpiCurrency(activitySummary.orders.month.gross_total) }}</strong></div>
                        </div>
                        <section class="kpi-yearly-orders" :aria-busy="yearlyOrdersLoading">
                            <div class="kpi-yearly-heading">
                                <span class="kpi-label">Jahresübersicht</span>
                                <div class="kpi-yearly-select">
                                    <label for="order-summary-year">Jahr</label>
                                    <select id="order-summary-year" v-model.number="orderSummaryYear">
                                        <option v-for="year in orderSummaryYears" :key="year" :value="year">{{ year }}</option>
                                    </select>
                                </div>
                            </div>
                            <p v-if="yearlyOrdersLoading && !yearlyOrders" class="kpi-yearly-message" role="status">Jahresübersicht wird geladen …</p>
                            <div v-else-if="yearlyOrdersError" class="kpi-yearly-message" role="alert">
                                {{ yearlyOrdersError }}
                                <button type="button" @click="loadActivitySummary">Erneut laden</button>
                            </div>
                            <table v-else-if="yearlyOrders" class="kpi-yearly-table" :aria-label="`Bestellungen im Jahr ${orderSummaryYear}`">
                                <thead>
                                    <tr><th scope="col">Monat</th><th scope="col">Anzahl</th><th scope="col">Bestellwert</th></tr>
                                </thead>
                                <tbody>
                                    <tr v-for="month in yearlyOrders.months" :key="month.month">
                                        <th scope="row">{{ orderMonthLabel(month.month) }}</th>
                                        <td>{{ formatOrderCount(month) }}</td>
                                        <td>{{ formatKpiCurrency(month.gross_total) }}</td>
                                    </tr>
                                </tbody>
                                <tfoot>
                                    <tr><th scope="row">Gesamt</th><td>{{ formatOrderCount(yearlyOrders.total) }}</td><td>{{ formatKpiCurrency(yearlyOrders.total.gross_total) }}</td></tr>
                                </tfoot>
                            </table>
                        </section>
                    </div>
                </article>
                <article class="kpi-card">
                    <h3>Projekte</h3>
                    <div class="kpi-card-body">
                        <div class="kpi-project-grid">
                            <div><span class="kpi-label"><i class="kpi-color kpi-red"></i>Projekte</span><strong>{{ formatKpiInteger(activitySummary.projects.red) }}</strong></div>
                            <button
                                ref="openProjectsTrigger"
                                type="button"
                                class="kpi-project-link"
                                :aria-expanded="showOpenProjects && openProjectsStatus === 'yellow'"
                                aria-controls="open-projects-panel"
                                :disabled="openingProjectId !== null"
                                @click="openOpenProjects('yellow')"
                            >
                                <span class="kpi-label"><i class="kpi-color kpi-yellow"></i>Anfragen</span>
                                <strong>{{ formatKpiInteger(activitySummary.projects.yellow) }}</strong>
                                <span class="kpi-project-link-hint">Offene Anfragen anzeigen</span>
                            </button>
                            <div><span class="kpi-label"><i class="kpi-color kpi-green"></i>Bestellungen</span><strong>{{ formatKpiInteger(activitySummary.projects.green) }}</strong></div>
                            <button
                                ref="openOffersTrigger"
                                type="button"
                                class="kpi-project-link"
                                :aria-expanded="showOpenProjects && openProjectsStatus === 'blue'"
                                aria-controls="open-projects-panel"
                                :disabled="openingProjectId !== null"
                                @click="openOpenProjects('blue')"
                            >
                                <span class="kpi-label"><i class="kpi-color kpi-blue"></i>Angebote</span>
                                <strong>{{ formatKpiInteger(activitySummary.projects.blue) }}</strong>
                                <span class="kpi-project-link-hint">Offene Angebote anzeigen</span>
                            </button>
                            <div><span class="kpi-label"><i class="kpi-color kpi-gray"></i>Absagen</span><strong>{{ formatKpiInteger(activitySummary.projects.gray) }}</strong></div>
                        </div>
                        <div class="kpi-total">
                            <span class="kpi-label">Gesamt</span>
                            <strong>{{ formatKpiInteger(activitySummary.projects.total) }}</strong>
                        </div>
                    </div>
                </article>
            </div>
        </section>
        <OpenProjectsComponent
            v-if="isInternalAdmin && showOpenProjects"
            id="open-projects-panel"
            ref="openProjects"
            :key="`${openProjectsStatus}-${activitySummaryCompanyId || 'all'}`"
            :status="openProjectsStatus"
            :company-id="activitySummaryCompanyId"
            :opening-project-id="openingProjectId"
            @open-project="configureOrder($event, { fromOpenProjects: true })"
            @close="closeOpenProjects"
            @loaded="loadActivitySummary"
        />
        <div v-if="isInternalAdmin" class="admin-context">
            <label for="admin-company-search">Unternehmen auswählen</label>
            <div class="company-search" ref="companySearch">
                <input
                    id="admin-company-search"
                    type="search"
                    v-model="companySearchTerm"
                    placeholder="Kundennummer, Unternehmen oder PLZ/Ort suchen"
                    autocomplete="off"
                    @focus="openCompanyResults"
                    @input="handleCompanySearchInput"
                    @keydown.down.prevent="moveCompanyHighlight(1)"
                    @keydown.up.prevent="moveCompanyHighlight(-1)"
                    @keydown.enter.prevent="selectHighlightedCompany"
                    @keydown.escape="closeCompanyResults"
                    @keydown.tab="closeCompanyResults"
                />
                <div v-if="showCompanyResults && companySearchResults.length" class="company-search-results">
                    <button
                        v-for="(companyOption, index) in companySearchResults"
                        :key="companyOption.id"
                        type="button"
                        :class="['company-search-result', { 'is-active': index === companyHighlightedIndex }]"
                        @mouseenter="companyHighlightedIndex = index"
                        @mousedown.prevent="selectCompany(companyOption)"
                    >
                        {{ companyOptionLabel(companyOption) }}
                    </button>
                </div>
                <div v-else-if="showCompanyResults && companySearchTerm.trim().length >= 2" class="company-search-empty">
                    Kein Unternehmen gefunden
                </div>
            </div>
            <div v-if="selectedCompany" class="admin-context-meta">
                {{ companyOptionLabel(selectedCompany) }}
            </div>
            <button
                v-if="ownCompany && Number(selectedCompanyId) !== Number(ownCompany.id)"
                type="button"
                class="button-with-icon admin-context-return"
                :title="companyOptionLabel(ownCompany)"
                :disabled="isLoading || openingProjectId !== null"
                @click="selectCompany(ownCompany)"
            >
                Zum eigenen Unternehmen
            </button>
        </div>
      <!-- Ersetzen Sie dies mit dem tatsächlichen Inhalt basierend auf dem Bild -->
        <div class="flex-top dashboard-card-row">
            <div class="new-order">
                <div class="header">Neuer Auftrag</div>
                <div class="flex-down">
                    <img :src="require('@/assets/dashboard/file-solid-dark-grey.svg')" alt="Facebook">
                    <input
                        type="text"
                        v-model="newProjectName"
                        :placeholder="newProjectPlaceholder"
                        maxlength="30"
                        @keydown.enter.prevent="addNewProject"
                    />
                    <div
                        v-if="canSelectProjectOwner"
                        class="user-search"
                        ref="userSearch"
                    >
                        <input
                            type="search"
                            v-model="userSearchTerm"
                            placeholder="Benutzer auswählen (Pflichtfeld)"
                            autocomplete="off"
                            aria-label="Projekt-Benutzer"
                            aria-required="true"
                            :aria-invalid="selectedCompanyUserId ? 'false' : 'true'"
                            required
                            role="combobox"
                            :aria-expanded="showUserResults ? 'true' : 'false'"
                            aria-controls="user-search-results"
                            :aria-activedescendant="activeUserResultId"
                            @focus="openUserResults"
                            @input="handleUserSearchInput"
                            @keydown.down.prevent="moveUserHighlight(1)"
                            @keydown.up.prevent="moveUserHighlight(-1)"
                            @keydown.enter.prevent="selectHighlightedUser"
                            @keydown.escape="closeUserResults"
                            @keydown.tab="closeUserResults"
                        />
                        <div
                            v-if="showUserResults && (userSearchResults.length || userSearchTerm.trim().length >= 2)"
                            id="user-search-results"
                            class="user-search-results"
                            role="listbox"
                        >
                            <button
                                v-for="(companyUser, index) in userSearchResults"
                                :key="companyUser.id"
                                :id="userResultId(companyUser)"
                                type="button"
                                role="option"
                                :aria-selected="userHighlightedIndex === index ? 'true' : 'false'"
                                :class="['user-search-result', { 'is-active': userHighlightedIndex === index }]"
                                @mouseenter="userHighlightedIndex = index"
                                @mousedown.prevent="selectCompanyUser(companyUser)"
                            >
                                <span class="user-search-result-name">{{ userDisplayName(companyUser) }}</span>
                                <span v-if="userDisplayEmail(companyUser)" class="user-search-result-email">{{ userDisplayEmail(companyUser) }}</span>
                                <span v-if="companyUser.is_blocked" class="user-search-result-status">blockiert</span>
                            </button>
                            <div v-if="userSearchTerm.trim().length >= 2 && !userSearchResults.length" class="user-search-empty">
                                Kein Benutzer gefunden
                            </div>
                        </div>
                    </div>
                    <button class="button-with-icon" @click="addNewProject" :disabled="isInternalAdmin && (!selectedCompanyId || !selectedCompanyUserId)">
                        <img class="icon-in-button" :src="require('@/assets/dashboard/paperclip-solid-dark-grey.svg')" alt="Icon" />
                        Jetzt anlegen
                    </button>
                </div>
                <!-- Fügen Sie weitere Felder entsprechend dem Bild hinzu -->
            </div>
            <div class="your-projects">
                <div class="header project-list-header">
                    <span class="project-list-title">{{ projectListTitle }}</span>
                    <button
                        type="button"
                        class="project-list-filter-button"
                        :class="{ 'is-active': hideOrderedProjects }"
                        :title="orderedProjectFilterTitle"
                        :aria-label="orderedProjectFilterTitle"
                        :aria-pressed="hideOrderedProjects ? 'true' : 'false'"
                        @click="hideOrderedProjects = !hideOrderedProjects"
                    >
                        <svg
                            class="project-list-filter-icon"
                            viewBox="0 0 32 32"
                            aria-hidden="true"
                            focusable="false"
                        >
                            <path d="M8 5h12l4 4v18H8z" />
                            <path d="M20 5v5h4" />
                            <path d="M11 14h6" />
                            <path d="M11 18h10" />
                            <path d="M11 22h5" />
                            <path d="M18.2 23.2l2 2 4.2-4.6" />
                            <path
                                v-if="hideOrderedProjects"
                                class="project-list-filter-slash"
                                d="M6 26L26 6"
                            />
                        </svg>
                    </button>
                </div>
                <div class="flex-down">
                    <div class="content project-row" v-for="project in visibleProjects" :key="project.id">
                        <div class="project-left">
                            <div
                                class="pdf-indicator-wrapper"
                                title="PDFs ansehen/herunterladen"
                                aria-label="PDFs ansehen"
                                role="button"
                                @click="openPdfModal(project.id)"
                                tabindex="0"
                            >
                                <span class="pdf-indicator" :class="getPdfStatusClass(project)"></span>
                            </div>
                            <div
                                @click="handleProjectMainClick(project)"
                                class="content-text project-main"
                                :class="{
                                    'project-main-editing': isRenamingProject(project),
                                    'project-main-opening': openingProjectId === project.id,
                                }"
                                :title="projectTooltip(project)"
                            >
                                <span v-if="!isRenamingProject(project)" class="project-name" :title="project.name">{{ project.name }}</span>
                                <input
                                    v-else
                                    v-model="projectRenameDraft"
                                    class="project-name-input"
                                    type="text"
                                    maxlength="30"
                                    aria-label="Projektname"
                                    :title="projectRenameDraft"
                                    :data-project-rename-input="project.id"
                                    @click.stop
                                    @keydown.enter.prevent="saveProjectRename(project)"
                                    @keydown.escape.prevent="cancelProjectRename"
                                />
                                <span v-if="showProjectOwnerInList" class="project-owner">
                                    {{ projectOwnerLabel(project) }}
                                </span>
                                <span v-if="project.created_at" class="project-created-at">
                                    Erstellt: {{ formatDateTime(project.created_at) }}
                                </span>
                            </div>
                        </div>
                        <div class="project-actions">
                            <template v-if="isRenamingProject(project)">
                                <button
                                    type="button"
                                    :disabled="isSavingProjectName"
                                    title="Projektname speichern"
                                    aria-label="Projektname speichern"
                                    @click.stop="saveProjectRename(project)"
                                >
                                    <i class="fa fa-save"></i>
                                </button>
                                <button
                                    type="button"
                                    :disabled="isSavingProjectName"
                                    title="Bearbeitung abbrechen"
                                    aria-label="Bearbeitung abbrechen"
                                    @click.stop="cancelProjectRename"
                                >
                                    <i class="fa fa-times"></i>
                                </button>
                            </template>
                            <template v-else>
                                <button
                                    v-if="canRenameProject(project)"
                                    type="button"
                                    title="Projektname bearbeiten"
                                    aria-label="Projektname bearbeiten"
                                    @click.stop="startProjectRename(project)"
                                >
                                    <i class="fa fa-pencil"></i>
                                </button>
                                <button v-if="canDuplicateProject(project)" type="button" @click.stop="confirmProjectDuplicate(project)"><i class="fa fa-copy"></i></button>
                                <button v-if="canDeleteProject(project)" type="button" @click.stop="confirmProjectDelete(project)"><i class="fa fa-trash"></i></button>
                            </template>
                        </div>
                    </div>
                </div>
                <!-- <div class="your-projects-buttons">
                    <button>Meine Projekte</button>
                    <button>Meine Angebote</button>
                    <button>Meine Aufträge</button>
                </div> -->
                <div v-if="canToggleProjectUserFilter" class="your-projects-buttons">
                    <button
                        type="button"
                        :class="{ 'is-active': projectFilterMode === 'all' }"
                        @click="setProjectFilterMode('all')"
                    >
                        Alle Projekte
                    </button>
                    <button
                        type="button"
                        :class="{ 'is-active': projectFilterMode === 'user' }"
                        @click="setProjectFilterMode('user')"
                    >
                        Meine Projekte
                    </button>
                    <button
                        v-if="isInternalAdmin"
                        type="button"
                        :class="{ 'is-active': projectFilterMode === 'ms' }"
                        @click="setProjectFilterMode('ms')"
                    >
                        MS Projekte
                    </button>
                </div>
                <!-- Fügen Sie weitere Felder entsprechend dem Bild hinzu -->
            </div>
            <div class="link-section">
                <div
                    class="header configurator-updates-link"
                    :class="{ 'configurator-updates-link--unseen': !hasSeenConfiguratorNews }"
                    @click="openConfiguratorNews"
                >
                    <i class="fa-solid fa-bullhorn" aria-hidden="true"></i>Neu im Konfigurator
                </div>
                <div class="header" @click="configureProfile"><img :src="require('@/assets/dashboard/user-solid-white.svg')" alt="Icon">Mein Profil</div>
                <div class="header" @click="openMarketing"><img :src="require('@/assets/dashboard/sheet-plastic-solid-white.svg')" alt="Icon">Marketing</div>
                <div class="header" @click="openNews"><img :src="require('@/assets/dashboard/info-solid-white.svg')" alt="Icon">News</div>
                <div class="header" @click="openWebsite"><img :src="require('@/assets/dashboard/globe-solid-white.svg')" alt="Icon">Zur Webseite von m&s</div>
                <div class="header" @click="logout"><i class="fa fa-right-from-bracket" aria-hidden="true"></i>Abmelden</div>
                <div class="media-links">
                    <img :src="require('@/assets/footer/Icon-awesome-facebook.svg')" alt="Facebook" @click="openSocial('facebook')" style="cursor: pointer;">
                    <img :src="require('@/assets/footer/Icon-awesome-instagram.svg')" alt="Instagram" @click="openSocial('instagram')" style="cursor: pointer;">
                    <img :src="require('@/assets/footer/Icon-awesome-youtube.svg')" alt="Youtube" @click="openSocial('youtube')" style="cursor: pointer;">
                    <img :src="require('@/assets/footer/Icon-awesome-whatsapp-square.svg')" alt="Whatsapp" @click="openSocial('whatsapp')" style="cursor: pointer;">
                </div>
                <!-- Fügen Sie weitere Felder entsprechend dem Bild hinzu -->
            </div>
        </div>
        <!-- <div class="iframe-news">
            <iframe src="https://sander-gruppe.eu/karriere/"></iframe>
        </div> -->
        <div class="flex-bottom">
            <div @click="openVeranstaltung" class="picture-section">
                <img src="https://p684534.mittwaldserver.info/storage/veranstaltungen.jpg" alt="News">
                <!-- Fügen Sie weitere Felder entsprechend dem Bild hinzu -->
            </div>
            <div @click="openFreetext" class="freetext-section">
                <img src="https://p684534.mittwaldserver.info/storage/news.jpg" alt="News">
                <!-- Fügen Sie weitere Felder entsprechend dem Bild hinzu -->
            </div>
        </div>
        <LegalFooter />
      <!-- Fügen Sie weitere Abschnitte entsprechend dem Bild hinzu -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="spinner"></div>
      </div>
        <div v-if="pdfModalProjectId" class="pdf-modal-overlay" @click.self="closePdfModal">
            <div class="pdf-modal">
                <div class="pdf-modal-header">
                    <span>Projekt-PDFs</span>
                    <button class="pdf-modal-close" @click="closePdfModal">×</button>
                </div>
                <div class="pdf-modal-content">
                    <div class="pdf-modal-columns">
                        <div class="pdf-modal-column">
                            <div class="tooltip-title">Bestellungen</div>
                            <div v-if="modalPdfEntry.orders.length" class="pdf-rows">
                                <div class="pdf-row" v-for="pdf in modalPdfEntry.orders" :key="pdf.id">
                                    <span class="pdf-date">{{ formatDate(pdf.created_at) }}</span>
                                    <a href="#" @click.prevent="downloadPdf(pdf.id)">{{ pdf.filename }}</a>
                                </div>
                            </div>
                            <div v-else class="tooltip-empty">Keine Bestellungen vorhanden</div>
                        </div>
                        <div class="pdf-modal-column">
                            <div class="tooltip-title">Anfragen</div>
                            <div v-if="modalPdfEntry.requests.length" class="pdf-rows">
                                <div class="pdf-row" v-for="pdf in modalPdfEntry.requests" :key="pdf.id">
                                    <span class="pdf-date">{{ formatDate(pdf.created_at) }}</span>
                                    <a href="#" @click.prevent="downloadPdf(pdf.id)">{{ pdf.filename }}</a>
                                </div>
                            </div>
                            <div v-else class="tooltip-empty">Keine Anfragen vorhanden</div>
                        </div>
                        <div class="pdf-modal-column">
                            <div class="tooltip-title">Angebote</div>
                            <div v-if="modalPdfEntry.offers.length" class="pdf-rows">
                                <div class="pdf-row" v-for="pdf in modalPdfEntry.offers" :key="pdf.id">
                                    <span class="pdf-date">{{ formatDate(pdf.created_at) }}</span>
                                    <a href="#" @click.prevent="downloadPdf(pdf.id)">{{ pdf.filename }}</a>
                                </div>
                            </div>
                            <div v-else class="tooltip-empty">Keine Angebote vorhanden</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="showConfiguratorNewsModal" class="news-modal-overlay" @click.self="closeConfiguratorNewsModal">
            <div class="news-modal" role="dialog" aria-modal="true" aria-labelledby="configurator-news-modal-title">
                <div class="news-modal-header">
                    <div class="news-modal-heading">
                        <span class="news-modal-icon" aria-hidden="true"><i class="fa-solid fa-bullhorn"></i></span>
                        <div>
                            <h2 id="configurator-news-modal-title" class="news-modal-title">Aktuelle Neuerungen im Konfigurator</h2>
                            <p class="news-modal-subtitle">Neue Funktionen und Verbesserungen</p>
                        </div>
                    </div>
                    <button class="news-modal-close" type="button" aria-label="Aktuelle Neuerungen im Konfigurator schließen" @click="closeConfiguratorNewsModal">×</button>
                </div>
                <iframe
                    class="news-modal-frame"
                    :src="configuratorNewsFrameSrc"
                    title="Neu im Konfigurator"
                    loading="lazy"
                ></iframe>
            </div>
        </div>
        <confirmation-dialog 
            v-if="showProjectDuplicateDialog" 
            :message="'Geben Sie den neuen Namen für das Projekt ein:'" 
            v-model:modelValue="duplicateProjectName"
            :showInput="true"
            placeholder="Neuer Projektname"
            :maxLength="30"
            @confirm="duplicateProject"
            @cancel="cancelProjectDuplicate"
        />
        <confirmation-dialog 
        v-if="showProjectDeleteConfirmation" 
            :message="'Möchten Sie das Projekt wirklich löschen?'" 
            @confirm="deleteProject"
            @cancel="cancelProjectDelete"
        />
        <document-acceptance-dialog
            v-if="showDocumentAcceptanceDialog"
            :documents="documentAcceptanceDocuments"
            :privacy-url="documentAcceptancePrivacyUrl"
            :loading="documentAcceptanceLoading"
            :error="documentAcceptanceError"
            @accept="acceptDocumentVersions"
            @cancel="cancelDocumentAcceptance"
        />
    </div>
</template>
  
<script>
import api from '@/api/client';
import ConfirmationDialog from './ConfirmationDialog.vue';
import DocumentAcceptanceDialog from './DocumentAcceptanceDialog.vue';
import LegalFooter from './LegalFooter.vue';
import OpenProjectsComponent from './OpenProjectsComponent.vue';

const SEARCH_RESULT_LIMIT = 200;

  export default {
    name: 'DashboardComponent',
    components: {
        ConfirmationDialog,
        DocumentAcceptanceDialog,
        LegalFooter,
        OpenProjectsComponent,
    },
    data() {
      return {
        company: {
          customerId: '',
          name: ''
        },
        contact: null,
        notes: {
          content: ''
        },
        newProjectName: '',
        projects: [],
        isProjectCreated: false,
        error: false,
        errorMessage: '',
        isLoading: false,
        pdfMap: {},
        pdfModalProjectId: null,
        showConfiguratorNewsModal: false,
        configuratorNewsFrameSrc: '',
        showProjectDeleteConfirmation: false,
        projectToDelete: null,
        duplicateProjectName: '',
        showProjectDuplicateDialog: false,
        projectToDuplicate: null,
        companies: [],
        selectedCompanyId: '',
        companySearchTerm: '',
        showCompanyResults: false,
        companyHighlightedIndex: -1,
        companyUsers: [],
        selectedCompanyUserId: '',
        userSearchTerm: '',
        showUserResults: false,
        userHighlightedIndex: -1,
        projectFilterMode: 'user',
        hideOrderedProjects: true,
        activitySummary: {
          users_total: 0,
          active_users_total: 0,
          online_total: 0,
          ms_users_online: 0,
          customer_users_online: 0,
          online_users: [],
          orders: {
            today: { count: 0, prl_count: 0, gross_total: 0 },
            week: { count: 0, prl_count: 0, gross_total: 0 },
            month: { count: 0, prl_count: 0, gross_total: 0 },
          },
          projects: { red: 0, yellow: 0, green: 0, blue: 0, gray: 0, total: 0 },
        },
        activitySummaryIntervalId: null,
        activitySummaryRequestId: 0,
        orderSummaryYear: new Date().getFullYear(),
        orderSummaryYears: [new Date().getFullYear()],
        yearlyOrders: null,
        yearlyOrdersCompanyId: null,
        yearlyOrdersLoading: false,
        yearlyOrdersError: '',
        kpiDashboardExpanded: false,
        showOpenProjects: false,
        openProjectsStatus: 'yellow',
        showDocumentAcceptanceDialog: false,
        documentAcceptanceDocuments: [],
        documentAcceptancePrivacyUrl: '',
        documentAcceptanceLoading: false,
        documentAcceptanceError: '',
        pendingDocumentAcceptanceAction: null,
        projectRenameProjectId: null,
        projectRenameDraft: '',
        isSavingProjectName: false,
        openingProjectId: null,
      }
    },
    computed: {
    loggedInUser() {
        return this.$parent.getLoggedInUser() || null;
    },
    isInternalAdmin() {
        const role = this.loggedInUser?.role;
        return role === 'internal_admin' || role === 'admin';
    },
    isCompanyAdmin() {
        return this.loggedInUser?.role === 'company_admin';
    },
    activeCompanyId() {
        if (this.isInternalAdmin) {
            return this.selectedCompanyId || null;
        }
        return this.loggedInUser?.unternehmen_id || null;
    },
    activitySummaryCompanyId() {
        const companyId = Number(this.selectedCompanyId);
        return this.isInternalAdmin && companyId && companyId !== Number(this.loggedInUser?.unternehmen_id)
            ? companyId
            : null;
    },
    selectedCompany() {
        const selectedId = Number(this.selectedCompanyId);
        return this.companies.find(company => Number(company.id) === selectedId) || null;
    },
    ownCompany() {
        const companyId = Number(this.loggedInUser?.unternehmen_id);
        return this.companies.find(company => Number(company.id) === companyId) || null;
    },
    sortedCompanies() {
        return [...this.companies].sort((a, b) => this.companyOptionLabel(a).localeCompare(
            this.companyOptionLabel(b),
            'de',
            { sensitivity: 'base', numeric: true }
        ));
    },
    companySearchResults() {
        const term = this.normalizeSearch(this.companySearchTerm);
        if (term.length < 2) {
            return [];
        }

        return this.sortedCompanies
            .filter(company => this.normalizeSearch(this.companyOptionSearchText(company)).includes(term))
            .slice(0, SEARCH_RESULT_LIMIT);
    },
    sortedCompanyUsers() {
        return [...this.companyUsers].sort((a, b) => this.userOptionLabel(a).localeCompare(
            this.userOptionLabel(b),
            'de',
            { sensitivity: 'base', numeric: true }
        ));
    },
    userSearchResults() {
        const term = this.normalizeSearch(this.userSearchTerm);
        if (!term) {
            return this.sortedCompanyUsers.slice(0, SEARCH_RESULT_LIMIT);
        }
        if (term.length < 2) {
            return [];
        }

        return this.sortedCompanyUsers
            .filter(user => this.normalizeSearch(this.userOptionLabel(user)).includes(term))
            .slice(0, SEARCH_RESULT_LIMIT);
    },
    activeUserResultId() {
        const highlightedUser = this.userSearchResults[this.userHighlightedIndex];
        return highlightedUser ? this.userResultId(highlightedUser) : null;
    },
    selectedCompanyUser() {
        const selectedId = Number(this.selectedCompanyUserId);
        return this.companyUsers.find(user => Number(user.id) === selectedId) || null;
    },
    activeProjectUserFilterId() {
        if (this.projectFilterMode !== 'user') {
            return null;
        }
        if (this.isInternalAdmin && this.selectedCompanyUserId) {
            return this.selectedCompanyUserId;
        }
        return this.loggedInUser?.id || null;
    },
    projectListTitle() {
        if (this.projectFilterMode === 'ms') {
            return this.selectedCompany ? `MS Projekte: ${this.selectedCompany.name}` : 'MS Projekte';
        }
        if (this.projectFilterMode === 'user') {
            if (this.isInternalAdmin && this.selectedCompanyUser) {
                return `Projekte: ${this.userDisplayName(this.selectedCompanyUser)}`;
            }
            return 'Meine Projekte';
        }
        if (this.isInternalAdmin) {
            return this.selectedCompany ? `Projekte: ${this.selectedCompany.name}` : 'Kundenprojekte';
        }
        if (this.isCompanyAdmin) {
            return 'Alle Projekte';
        }
        return 'Meine Projekte';
    },
    canToggleProjectUserFilter() {
        return this.isInternalAdmin || this.isCompanyAdmin;
    },
    visibleProjects() {
        if (!this.hideOrderedProjects) {
            return this.projects;
        }

        return this.projects.filter(project => !this.isProjectOrdered(project));
    },
    orderedProjectFilterTitle() {
        return this.hideOrderedProjects
            ? 'Bestellte Projekte einblenden'
            : 'Bestellte Projekte ausblenden';
    },
    canSelectProjectOwner() {
        return this.isInternalAdmin && !!this.selectedCompanyId;
    },
    showProjectOwnerInList() {
        return this.isInternalAdmin || this.isCompanyAdmin;
    },
    newProjectPlaceholder() {
        return this.isInternalAdmin ? 'Projekt für Kunden eingeben' : 'Projektnamen eingeben';
    },
    contactImageUrl() {
        if (this.contact && this.contact.kuerzel) {
            return `https://p684534.mittwaldserver.info/storage/ansprechpartner/${this.contact.kuerzel}.jpg`;
        }
        if (this.contact && this.contact.foto) {
            return this.contact.foto;
        }
            return require('@/assets/dashboard/Icon-awesome-user.svg');
        },
    modalPdfEntry() {
        const entry = this.pdfMap[this.pdfModalProjectId];
        return entry
            ? {
                orders: entry.orders || [],
                requests: entry.requests || [],
                offers: entry.offers || [],
            }
            : { orders: [], requests: [], offers: [] };
    },
    configuratorNewsContentPath() {
        const basePath = process.env.BASE_URL || '/';
        return `${basePath.replace(/\/?$/, '/')}storage/neu-im-konfigurator.html`;
    },
    hasSeenConfiguratorNews() {
        return this.loggedInUser?.configurator_news_seen === true;
    },
    },
    watch: {
        activitySummaryCompanyId() {
            this.loadActivitySummary();
        },
        orderSummaryYear() {
            this.loadActivitySummary();
        },
    },
    mounted() {
        document.addEventListener('mousedown', this.handleDocumentMouseDown);
        document.addEventListener('keydown', this.handleDocumentKeyDown);
    },
    beforeUnmount() {
        document.removeEventListener('mousedown', this.handleDocumentMouseDown);
        document.removeEventListener('keydown', this.handleDocumentKeyDown);
        this.stopActivitySummaryPolling();
    },
    methods: {
        actorParams() {
            return {};
        },
        formatKpiInteger(value) {
            return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 })
                .format(Number(value) || 0);
        },
        formatOrderCount(period) {
            return `${this.formatKpiInteger(period?.count)} (${this.formatKpiInteger(period?.prl_count)})`;
        },
        orderMonthLabel(month) {
            return new Intl.DateTimeFormat('de-DE', { month: 'long' }).format(new Date(2000, month - 1, 1));
        },
        formatKpiCurrency(value) {
            return new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(Number(value) || 0);
        },
        onlineUserLabel(user) {
            const name = String(user?.name || '').trim() || 'Unbekannter Nutzer';
            const companyDetails = [user?.company_name, user?.company_location]
                .map(value => String(value || '').trim())
                .filter(Boolean);

            return companyDetails.length
                ? `${name} (${companyDetails.join(', ')})`
                : name;
        },
        wasProjectCreatedByInternalAdmin(project) {
            const creatorRole = project?.created_by?.role;
            return creatorRole === 'internal_admin' || creatorRole === 'admin';
        },
        canDuplicateProject(project) {
            return !!project?.id;
        },
        canDeleteProject(project) {
            if (this.isProjectOrdered(project)) {
                return false;
            }

            if (!this.isInternalAdmin) {
                return !this.isProjectLockedByInternalAdmin(project);
            }

            return project?.internal_admin_edited_by_shop_user_id != null;
        },
        canRenameProject(project) {
            return !!project?.id && this.canDeleteProject(project);
        },
        isRenamingProject(project) {
            return this.projectRenameProjectId === project?.id;
        },
        isProjectLockedByInternalAdmin(project) {
            return !!project?.internal_admin_edited_at;
        },
        isProjectOrdered(project) {
            const pdfEntry = project?.id ? this.pdfMap[project.id] : null;
            return !!(
                project?.manual_offer_ordered_at ||
                project?.has_order ||
                project?.hasOrder ||
                project?.order_sent_at ||
                project?.orderSentAt ||
                pdfEntry?.orders?.length
            );
        },
        projectWithPdfState(project) {
            const orders = this.pdfMap[project?.id]?.orders || [];

            return {
                ...project,
                has_order: this.isProjectOrdered(project),
                order_sent_at: project?.order_sent_at || orders[0]?.created_at || null,
            };
        },
        companyOptionLabel(companyOption) {
            const kdnr = companyOption.kdnr ? `${companyOption.kdnr} - ` : '';
            const plzort = this.companyPostalCodeAndCity(companyOption);
            return `${kdnr}${companyOption.name}${plzort ? ` - ${plzort}` : ''}`;
        },
        companyPostalCodeAndCity(companyOption) {
            return String(companyOption?.plzort || companyOption?.PLZort || '').trim();
        },
        companyOptionSearchText(companyOption) {
            const plzort = companyOption.plzort || companyOption.PLZort || '';
            return `${this.companyOptionLabel(companyOption)} ${plzort}`;
        },
        normalizeSearch(value) {
            return String(value || '').trim().toLowerCase();
        },
        openCompanyResults() {
            this.showCompanyResults = true;
            this.companyHighlightedIndex = -1;
        },
        closeCompanyResults() {
            this.showCompanyResults = false;
            this.companyHighlightedIndex = -1;
        },
        handleCompanySearchInput() {
            this.showCompanyResults = true;
            this.companyHighlightedIndex = -1;
        },
        moveCompanyHighlight(direction) {
            const results = this.companySearchResults;
            if (!results.length) {
                this.companyHighlightedIndex = -1;
                this.showCompanyResults = true;
                return;
            }

            this.showCompanyResults = true;
            if (this.companyHighlightedIndex < 0) {
                this.companyHighlightedIndex = direction > 0 ? 0 : results.length - 1;
                return;
            }

            this.companyHighlightedIndex = (this.companyHighlightedIndex + direction + results.length) % results.length;
        },
        async selectHighlightedCompany() {
            const selected = this.companySearchResults[this.companyHighlightedIndex];
            if (selected) {
                await this.selectCompany(selected);
            }
        },
        async selectCompany(companyOption) {
            this.selectedCompanyId = companyOption.id;
            this.companySearchTerm = this.companyOptionLabel(companyOption);
            this.closeCompanyResults();
            await this.handleCompanyChange();
        },
        async openOpenProjects(status = 'yellow') {
            if (!this.isInternalAdmin || this.openingProjectId !== null) return;
            this.openProjectsStatus = status;
            this.showOpenProjects = true;
            await this.$nextTick();
            const panel = this.$refs.openProjects?.$el;
            panel?.focus({ preventScroll: true });
            panel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
        async closeOpenProjects() {
            this.showOpenProjects = false;
            await this.$nextTick();
            const trigger = this.openProjectsStatus === 'blue' ? this.$refs.openOffersTrigger : this.$refs.openProjectsTrigger;
            trigger?.focus();
        },
        async loadOpenProjectContext(project) {
            if (!this.isInternalAdmin) {
                throw new Error('Keine Berechtigung für die Übersicht offener Projekte.');
            }

            const projectResponse = await api.get(`/api/projekte/${project.id}`, {
                params: this.actorParams(),
            });
            const refreshedProject = projectResponse.data;
            const companyId = refreshedProject.unternehmen_id || refreshedProject.unternehmen?.id;
            if (!companyId) {
                throw new Error('Dem Projekt ist kein Unternehmen zugeordnet.');
            }

            // Die Projektrelation enthält nur Stammdaten. Für den Firmenwechsel
            // werden wie bei der Unternehmensauswahl die vollständigen Daten benötigt.
            const companyResponse = await api.get(`/api/unternehmen/${companyId}`, {
                params: this.actorParams(),
            });
            const company = companyResponse.data;
            if (!company?.id || Number(company.id) !== Number(companyId)) {
                throw new Error('Das Unternehmen des Projekts konnte nicht geladen werden.');
            }
            const companyIndex = this.companies.findIndex(item => Number(item.id) === Number(company.id));
            if (companyIndex === -1) {
                this.companies.push(company);
            } else {
                this.companies.splice(companyIndex, 1, company);
            }
            await this.selectCompany(company);

            return refreshedProject;
        },
        userOptionLabel(companyUser) {
            const name = companyUser.name || companyUser.email;
            const email = companyUser.email && companyUser.email !== name ? ` (${companyUser.email})` : '';
            const blocked = companyUser.is_blocked ? ' - blockiert' : '';
            return `${name}${email}${blocked}`;
        },
        userDisplayName(companyUser) {
            return companyUser?.name || companyUser?.email || 'Benutzer ohne Namen';
        },
        userDisplayEmail(companyUser) {
            const name = this.userDisplayName(companyUser);
            return companyUser?.email && companyUser.email !== name ? companyUser.email : '';
        },
        userResultId(companyUser) {
            return companyUser?.id ? `user-search-result-${companyUser.id}` : null;
        },
        handleDocumentMouseDown(event) {
            const companySearch = this.$refs.companySearch;
            const userSearch = this.$refs.userSearch;

            if (companySearch && !companySearch.contains(event.target)) {
                this.closeCompanyResults();
            }
            if (userSearch && !userSearch.contains(event.target)) {
                this.closeUserResults();
            }
        },
        handleDocumentKeyDown(event) {
            if (event.key === 'Escape') {
                this.closeConfiguratorNewsModal();
            }
        },
        openUserResults() {
            this.showUserResults = true;
            this.userHighlightedIndex = -1;
        },
        closeUserResults() {
            this.showUserResults = false;
            this.userHighlightedIndex = -1;
        },
        handleUserSearchInput() {
            const hadSelectedCompanyUser = !!this.selectedCompanyUserId;
            this.showUserResults = true;
            this.userHighlightedIndex = -1;
            this.selectedCompanyUserId = '';
            if (this.projectFilterMode === 'user' && hadSelectedCompanyUser) {
                this.getAllProjects();
            }
        },
        moveUserHighlight(direction) {
            const resultCount = this.userSearchResults.length;
            this.showUserResults = true;
            if (!resultCount) {
                this.userHighlightedIndex = -1;
                return;
            }

            if (this.userHighlightedIndex < 0) {
                this.userHighlightedIndex = direction > 0 ? 0 : resultCount - 1;
                this.$nextTick(this.scrollHighlightedUserIntoView);
                return;
            }

            this.userHighlightedIndex = (this.userHighlightedIndex + direction + resultCount) % resultCount;
            this.$nextTick(this.scrollHighlightedUserIntoView);
        },
        selectHighlightedUser() {
            const selectedIndex = this.userHighlightedIndex >= 0
                ? this.userHighlightedIndex
                : (this.userSearchResults.length === 1 ? 0 : -1);
            const selected = this.userSearchResults[selectedIndex];
            if (selected) {
                this.selectCompanyUser(selected);
            }
        },
        selectCompanyUser(companyUser) {
            this.selectedCompanyUserId = companyUser?.id || '';
            this.userSearchTerm = companyUser ? this.userOptionLabel(companyUser) : '';
            this.closeUserResults();
            if (this.projectFilterMode === 'user') {
                this.getAllProjects();
            }
        },
        scrollHighlightedUserIntoView() {
            const userSearch = this.$refs.userSearch;
            if (!userSearch || this.userHighlightedIndex < 0) {
                return;
            }

            const resultButtons = userSearch.querySelectorAll('.user-search-result');
            const activeResult = resultButtons[this.userHighlightedIndex];
            if (activeResult) {
                activeResult.scrollIntoView({ block: 'nearest' });
            }
        },
        isExternalMsProject(project) {
            const createdBy = project.created_by;
            const creatorRole = createdBy?.role;
            const wasCreatedByInternalAdmin = creatorRole === 'internal_admin' || creatorRole === 'admin';
            const projectCompanyId = project.unternehmen_id || project.unternehmen?.id;

            if (!wasCreatedByInternalAdmin || !projectCompanyId) {
                return false;
            }

            return String(projectCompanyId) !== String(createdBy?.unternehmen_id || '');
        },
        userInitials(user) {
            const value = String(user?.name || user?.email || '').trim();
            if (!value) {
                return '';
            }

            const nameParts = value
                .split(/\s+/)
                .filter(Boolean);

            if (nameParts.length > 1) {
                return nameParts
                    .slice(0, 2)
                    .map(part => part.charAt(0))
                    .join('')
                    .toUpperCase();
            }

            const emailName = value.split('@')[0];
            const emailParts = emailName
                .split(/[._-]+/)
                .filter(Boolean);

            if (emailParts.length > 1) {
                return emailParts
                    .slice(0, 2)
                    .map(part => part.charAt(0))
                    .join('')
                    .toUpperCase();
            }

            return emailName.slice(0, 2).toUpperCase();
        },
        msUserLabel(user) {
            const initials = this.userInitials(user);
            return initials ? `MS ${initials}` : 'MS';
        },
        projectOwnerLabel(project) {
            if (this.isInternalAdmin && !project?.shop_user_id && !project?.shop_user) {
                return 'Keinen Benutzer zugeordnet';
            }

            if (this.isExternalMsProject(project)) {
                return this.msUserLabel(project.created_by);
            }

            const owner = project.shop_user?.name || project.shop_user?.email;
            return owner ? `Benutzer: ${owner}` : '';
        },
        projectTooltip(project) {
            const ownerLabel = this.projectOwnerLabel(project);
            const owner = ownerLabel.replace(/^Benutzer:\s*/, '');
            const company = project.unternehmen?.name;

            if (owner && company) {
                return `${project.name} - ${owner}, ${company}`;
            }
            if (owner) {
                return `${project.name} - ${owner}`;
            }
            if (company) {
                return `${project.name} - ${company}`;
            }
            return project.name;
        },
        handleProjectMainClick(project) {
            if (this.isRenamingProject(project)) {
                return;
            }

            this.configureOrder(project);
        },
        startProjectRename(project) {
            if (!this.canRenameProject(project)) {
                this.showError('Der Projektname kann nach einer Bestellung nicht mehr geändert werden.');
                return;
            }

            this.projectRenameProjectId = project.id;
            this.projectRenameDraft = project.name || '';
            this.$nextTick(() => {
                const input = this.$el.querySelector(`[data-project-rename-input="${project.id}"]`);
                if (input) {
                    input.focus();
                    input.select();
                }
            });
        },
        cancelProjectRename() {
            this.projectRenameProjectId = null;
            this.projectRenameDraft = '';
        },
        async saveProjectRename(project) {
            if (!this.isRenamingProject(project) || this.isSavingProjectName) {
                return;
            }

            if (this.isProjectOrdered(project)) {
                this.showError('Der Projektname kann nach einer Bestellung nicht mehr geändert werden.');
                this.cancelProjectRename();
                return;
            }

            const nextName = this.projectRenameDraft.trim();
            if (!nextName) {
                this.showError('Bitte geben Sie einen gültigen Namen ein.');
                return;
            }

            if (nextName === project.name) {
                this.cancelProjectRename();
                return;
            }

            const projectExists = this.projects.some(existingProject =>
                existingProject.id !== project.id && existingProject.name === nextName
            );
            if (projectExists) {
                this.showError('Projekt bereits vorhanden!');
                return;
            }

            this.isSavingProjectName = true;
            try {
                const response = await api.put(`/api/projekte/${project.id}`, {
                    name: nextName,
                    ...this.actorParams(),
                });
                const updatedProject = response.data || {};

                this.projects = this.projects.map(existingProject => {
                    if (existingProject.id !== project.id) {
                        return existingProject;
                    }

                    return this.projectWithPdfState({
                        ...existingProject,
                        ...updatedProject,
                        name: updatedProject.name || nextName,
                    });
                });

                const selectedProject = this.$parent.getSelectedProject?.();
                if (selectedProject?.id === project.id) {
                    const refreshedProject = this.projects.find(existingProject => existingProject.id === project.id);
                    this.$parent.setSelectedProject(refreshedProject || {
                        ...selectedProject,
                        ...updatedProject,
                        name: updatedProject.name || nextName,
                    });
                }

                this.cancelProjectRename();
            } catch (error) {
                console.error('Fehler beim Aktualisieren des Projektnamens:', error);
                this.showError(error?.response?.data?.message || 'Der Projektname konnte nicht gespeichert werden.');
            } finally {
                this.isSavingProjectName = false;
            }
        },
        async loadCompanies() {
            if (!this.isInternalAdmin) {
                this.companies = [];
                return;
            }

            const response = await api.get('/api/unternehmen', {
                params: this.actorParams(),
            });
            this.companies = Array.isArray(response.data) ? response.data : [];
        },
        async loadCompanyUsers() {
            if (!this.isInternalAdmin || !this.selectedCompanyId) {
                this.companyUsers = [];
                this.selectedCompanyUserId = '';
                this.userSearchTerm = '';
                this.closeUserResults();
                return;
            }

            const response = await api.get(`/api/shopusers/unternehmen/${this.selectedCompanyId}`, {
                params: this.actorParams(),
            });
            this.companyUsers = Array.isArray(response.data) ? response.data : [];

            const selectedUserStillExists = this.companyUsers.some(user => Number(user.id) === Number(this.selectedCompanyUserId));
            if (!selectedUserStillExists) {
                this.selectedCompanyUserId = '';
                this.userSearchTerm = '';
            } else {
                const selectedUser = this.companyUsers.find(user => Number(user.id) === Number(this.selectedCompanyUserId));
                this.userSearchTerm = this.userOptionLabel(selectedUser);
            }
        },
        async loadSelectedCompanyContact() {
            if (!this.isInternalAdmin) {
                this.contact = this.$parent.getContactPerson();
                return;
            }

            this.syncSelectedCompanyToParent();
            const contactId = this.selectedCompany?.ansprechpartner_id;
            if (!contactId) {
                this.contact = null;
                this.$parent.setContactPerson(null);
                return;
            }

            try {
                const response = await api.get(`/api/ansprechpartner/${contactId}`);
                this.contact = response.data;
                this.$parent.setContactPerson(response.data);
            } catch (error) {
                console.error('Fehler beim Laden der Ansprechperson: ', error);
                this.contact = null;
                this.$parent.setContactPerson(null);
            }
        },
        syncSelectedCompanyToParent() {
            if (this.isInternalAdmin && this.selectedCompany) {
                this.$parent.setCompany(this.selectedCompany);
            }
        },
        async ensureCompanyContext() {
            if (!this.loggedInUser) {
                return;
            }

            if (!this.isInternalAdmin) {
                this.selectedCompanyId = this.loggedInUser.unternehmen_id || '';
                this.selectedCompanyUserId = '';
                this.companyUsers = [];
                this.userSearchTerm = '';
                this.closeUserResults();
                this.contact = this.$parent.getContactPerson();
                return;
            }

            await this.loadCompanies();
            if (!this.selectedCompanyId) {
                this.selectedCompanyId = this.loggedInUser.unternehmen_id || this.companies[0]?.id || '';
            }
            if (this.selectedCompany) {
                this.companySearchTerm = this.companyOptionLabel(this.selectedCompany);
            }
            await this.loadCompanyUsers();
            await this.loadSelectedCompanyContact();
        },
        async handleCompanyChange() {
            this.selectedCompanyUserId = '';
            this.userSearchTerm = '';
            this.projectFilterMode = 'all';
            this.closeUserResults();
            this.projects = [];
            this.pdfMap = {};
            this.$parent.setSelectedProject(null);
            await this.loadCompanyUsers();
            await this.loadSelectedCompanyContact();
            await this.getAllProjects();
        },
        formatTel(number) {
            if (!number) {
                return '';
            }
            const cleaned = number.toString().replace(/[^\d+]/g, '');
            return `tel:${cleaned}`;
        },
        formatYmd(dateObj) {
            const y = dateObj.getFullYear();
            const m = String(dateObj.getMonth() + 1).padStart(2, '0');
            const d = String(dateObj.getDate()).padStart(2, '0');
            return `${y}${m}${d}`;
        },
        async sha256Hex(message) {
            const encoder = new TextEncoder();
            const data = encoder.encode(message);
            const hashBuffer = await crypto.subtle.digest('SHA-256', data);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        },
        async openMarketing() {
            const canOpenMarketing = await this.ensureDocumentAcceptanceBeforeAction(() => this.openMarketingPortal());
            if (!canOpenMarketing) {
                return;
            }

            this.openMarketingPortal();
        },
        openMarketingPortal() {
            const secret = 'SuperGeheimesWort2026';
            const today = this.formatYmd(new Date());
            this.sha256Hex(secret + today).then((token) => {
                const dash = String.fromCharCode(45);
                const url = `https://sander${dash}gruppe.eu/marketingpaket/?access_token=${token}`;
                window.open(url, '_blank');
            });
        },
        async openFreetext() {
            this.openMarketing();
        },
        openNews() {
            window.open('https://www.ms-sprossenelemente.de/aktuelles/', '_blank');
        },
        async openConfiguratorNews() {
            this.configuratorNewsFrameSrc = `${this.configuratorNewsContentPath}?v=${Date.now()}`;
            this.showConfiguratorNewsModal = true;

            await this.markConfiguratorNewsAsSeen();
        },
        async markConfiguratorNewsAsSeen() {
            if (this.hasSeenConfiguratorNews) {
                return;
            }

            try {
                const response = await api.post('/api/shopusers/configurator-news/seen');
                const user = response.data?.user || { configurator_news_seen: true };
                this.$parent.patchLoggedInUser?.(user);
            } catch (error) {
                console.error('Fehler beim Aktualisieren des Konfigurator-Neuigkeiten-Status:', error);
            }
        },
        closeConfiguratorNewsModal() {
            this.showConfiguratorNewsModal = false;
        },
        openWebsite() {
                window.open('https://www.ms-sprossenelemente.de', '_blank');
        },
        openVeranstaltung() {
                window.open('https://www.ms-sprossenelemente.de/aktuelles/sander-partnertage/', '_blank');
        },
        openSocial(platform) {
            const targets = {
                facebook: 'https://www.facebook.com/msInsektenschutz',
                instagram: 'https://www.instagram.com/ms_insektenschutz/',
                youtube: 'https://www.youtube.com/@mssprossenelementegmbh9629/videos',
                whatsapp: 'https://wa.me/message/BWHQ35ZD2AQYJ1',
                linkedin: 'https://de.linkedin.com/company/m-s-sprossenelemente-gmbh',
                xing: 'https://www.xing.com/pages/m-ssprossenelementegmbh-insektenschutzgitteru-vorsatzsprossen',
            };
            const url = targets[platform];
            if (url) {
                window.open(url, '_blank');
            }
        },
        openPdfModal(projectId) {
            this.pdfModalProjectId = projectId;
        },
        closePdfModal() {
            this.pdfModalProjectId = null;
        },
        getPdfStatusClass(project) {
            const projectId = typeof project === 'object' ? project?.id : project;
            const entry = this.pdfMap[projectId];
            if (this.isProjectOrdered(typeof project === 'object' ? project : { id: projectId })) {
                return 'pdf-status-order';
            }
            if (project?.manual_offer_lost_at) {
                return 'pdf-status-lost';
            }
            if (
                project?.has_offer ||
                project?.hasOffer ||
                project?.offer_sent_at ||
                project?.offerSentAt ||
                project?.manual_offer_sent_at ||
                (entry && entry.offers && entry.offers.length)
            ) {
                return 'pdf-status-offer';
            }
            if (
                project?.has_request ||
                project?.hasRequest ||
                project?.request_sent_at ||
                project?.requestSentAt ||
                (entry && entry.requests && entry.requests.length)
            ) {
                return 'pdf-status-request';
            }
            return 'pdf-status-none';
        },
        async downloadPdf(pdfId) {
            try {
                const response = await api.get(`/api/projektpdfs/${pdfId}/download`, {
                    responseType: 'blob',
                });
                const contentDisposition = response.headers?.['content-disposition'] || '';
                const filename = this.extractFilenameFromDisposition(contentDisposition) || 'Projekt.pdf';
                const blob = new Blob([response.data], {
                    type: response.headers?.['content-type'] || 'application/pdf',
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Fehler beim PDF-Download: ', error);
                this.showError('Das PDF konnte nicht heruntergeladen werden.');
            }
        },
        extractFilenameFromDisposition(dispositionHeader) {
            if (!dispositionHeader) {
                return '';
            }

            const utf8Match = dispositionHeader.match(/filename\*=UTF-8''([^;]+)/i);
            if (utf8Match?.[1]) {
                try {
                    return decodeURIComponent(utf8Match[1]);
                } catch (error) {
                    return utf8Match[1];
                }
            }

            const asciiMatch = dispositionHeader.match(/filename="([^"]+)"/i) || dispositionHeader.match(/filename=([^;]+)/i);
            return asciiMatch?.[1]?.trim() || '';
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            return d.toLocaleDateString('de-DE');
        },
        formatDateTime(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;

            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');

            return `${day}.${month}.${year} ${hours}:${minutes}`;
        },
        confirmProjectDuplicate(project) {
            this.projectToDuplicate = project;
            this.duplicateProjectName = ''; // Input zurücksetzen
            this.showProjectDuplicateDialog = true;
        },
        cancelProjectDuplicate() {
            this.showProjectDuplicateDialog = false;
        },
        duplicateProject() {
            const duplicateName = this.duplicateProjectName.trim();
            if (!duplicateName) {
                this.showError('Bitte geben Sie einen gültigen Namen ein.');
                return;
            }
            const projectId = this.projectToDuplicate.id;
            const projectExists = this.projects.some(project => project.name === duplicateName);

            if (projectExists) {
                this.isLoading = false;
                this.showError('Projekt bereits vorhanden!');
                return;
            }

            // API-Aufruf für das Duplizieren
            api.post(`/api/projekte/${projectId}/duplicate`, {
                name: duplicateName,
                ...this.actorParams(),
            })
            .then(() => {
                this.showProjectDuplicateDialog = false;
                this.getAllProjects(); // Aktualisiere die Projektliste
            })
            .catch((error) => {
                console.error('Fehler beim Duplizieren des Projekts:', error);
                this.showError('Das Projekt konnte nicht dupliziert werden.');
            });
        },
        confirmProjectDelete(projectToDel) {
            if (!this.canDeleteProject(projectToDel)) {
                return;
            }

            this.projectToDelete = projectToDel;
            this.showProjectDeleteConfirmation = true;
        },
        cancelProjectDelete() {
            this.showProjectDeleteConfirmation = false;
        },
        async configureOrder(project, { fromOpenProjects = false } = {}) {
            if (this.openingProjectId !== null || !project?.id) {
                return;
            }

            this.openingProjectId = project.id;
            this.isLoading = true;

            try {
                const projectToOpen = this.projectWithPdfState(fromOpenProjects
                    ? await this.loadOpenProjectContext(project)
                    : project);
                this.isLoading = true;
                const canOpenConfigurator = await this.ensureDocumentAcceptanceBeforeAction(() => {
                    this.openProjectInConfigurator(projectToOpen);
                });
                if (!canOpenConfigurator) {
                    if (!this.showDocumentAcceptanceDialog) {
                        this.openingProjectId = null;
                    }
                    return;
                }

                this.openProjectInConfigurator(projectToOpen);
            } catch (error) {
                console.error('Fehler beim Öffnen des Projekts:', error);
                this.openingProjectId = null;
                this.showError(error?.response?.data?.message || error?.message || 'Das Projekt konnte nicht geöffnet werden.');
            } finally {
                this.isLoading = false;
            }
        },
        openProjectInConfigurator(project) {
            this.showOpenProjects = false;
            this.$parent.setSelectedProject(project);
            this.$parent.changeMenuState(1);
        },
        async ensureDocumentAcceptanceBeforeAction(action) {
            if (!this.loggedInUser?.id) {
                return false;
            }

            this.documentAcceptanceLoading = true;
            this.documentAcceptanceError = '';

            try {
                const response = await api.get('/api/document-acceptances/status', {
                    params: this.actorParams(),
                });

                if (!response.data?.required) {
                    return true;
                }

                this.pendingDocumentAcceptanceAction = action;
                this.documentAcceptanceDocuments = this.normalizeDocumentAcceptanceDocuments(response.data.documents || []);
                this.documentAcceptancePrivacyUrl = this.normalizeDocumentUrl(response.data.privacy_policy_url || '');
                this.showDocumentAcceptanceDialog = true;
                return false;
            } catch (error) {
                console.error('Fehler beim Prüfen der Nutzungsbedingungen:', error);
                this.showError('Die Nutzungsbedingungen konnten nicht geprüft werden.');
                return false;
            } finally {
                this.documentAcceptanceLoading = false;
            }
        },
        normalizeDocumentAcceptanceDocuments(documents) {
            return documents.map((document) => ({
                ...document,
                pdf_url: this.normalizeDocumentUrl(document.pdf_url || ''),
            }));
        },
        normalizeDocumentUrl(url) {
            const apiBase = String(api.defaults.baseURL || '').replace(/\/$/, '');
            const documentUrl = String(url || '');
            const isAbsoluteUrl = /^https?:\/\//i.test(documentUrl);

            return documentUrl && !isAbsoluteUrl && documentUrl.startsWith('/')
                ? `${apiBase}${documentUrl}`
                : documentUrl;
        },
        async acceptDocumentVersions(acceptedDocuments) {
            this.documentAcceptanceLoading = true;
            this.documentAcceptanceError = '';

            try {
                await api.post('/api/document-acceptances/accept', {
                    accepted_documents: acceptedDocuments,
                    ...this.actorParams(),
                });

                const pendingAction = this.pendingDocumentAcceptanceAction;
                this.pendingDocumentAcceptanceAction = null;
                this.showDocumentAcceptanceDialog = false;
                this.documentAcceptanceDocuments = [];
                this.documentAcceptancePrivacyUrl = '';

                if (pendingAction) {
                    pendingAction();
                }
            } catch (error) {
                console.error('Fehler beim Speichern der Nutzungsbedingungen:', error);
                this.documentAcceptanceError = error?.response?.data?.message
                    || 'Die Zustimmung konnte nicht gespeichert werden.';
            } finally {
                this.documentAcceptanceLoading = false;
            }
        },
        cancelDocumentAcceptance() {
            this.pendingDocumentAcceptanceAction = null;
            this.showDocumentAcceptanceDialog = false;
            this.documentAcceptancePrivacyUrl = '';
            this.documentAcceptanceError = '';
            this.openingProjectId = null;
        },
        configureProfile() {
            this.$parent.changeMenuState(4);
        },
        resolveNewProjectOwnerId() {
            const loggedInUser = this.loggedInUser;

            if (!loggedInUser?.id) {
                return null;
            }

            if (this.isInternalAdmin) {
                return this.selectedCompanyUserId
                    ? Number(this.selectedCompanyUserId)
                    : null;
            }

            return loggedInUser.id;
        },
        addNewProject() {
            this.isLoading = true;
            const projectName = this.newProjectName.trim();

            if (this.isInternalAdmin && !this.selectedCompanyId) {
                this.isLoading = false;
                this.showError('Bitte wählen Sie zuerst ein Unternehmen aus.');
                return;
            }

            if (this.isInternalAdmin && !this.selectedCompanyUserId) {
                this.isLoading = false;
                this.showError('Bitte wählen Sie zuerst einen Benutzer aus.');
                return;
            }

            if (projectName) {
                // Prüfen, ob der Projektname bereits existiert
                const projectExists = this.projects.some(project => project.name === projectName);

                if (projectExists) {
                    this.isLoading = false;
                    this.showError('Projekt bereits vorhanden!');
                    return;
                }

                const projectOwnerId = this.resolveNewProjectOwnerId();
                const payload = {
                    name: projectName,
                    unternehmen_id: this.activeCompanyId,
                    shop_user_id: projectOwnerId,
                    ...this.actorParams(),
                };

                api.post('/api/projekte', payload)
                    .then(() => {
                        this.newProjectName = '';
                        this.isProjectCreated = true;
                        this.getAllProjects();     
                    })
                    .catch(error => {
                        console.error('Es gab einen Fehler beim Hinzufügen des Projekts: ', error);
                        this.isLoading = false;
                        this.showError(error?.response?.data?.message || 'Das Projekt konnte nicht angelegt werden.');
                    });
            }
            else {
                this.isLoading = false;
            }
        },
        deleteProject() {
            this.showProjectDeleteConfirmation = false;
            api.delete(`/api/projekte/${this.projectToDelete.id}`, {
                params: this.actorParams(),
            })
                .then(() => {
                    this.refreshDashboard();
                })
                .catch(error => {
                    console.error('Fehler beim Laden der Projekte: ', error);
                });
            
        },
        async refreshDashboard() {
            this.openingProjectId = null;
            this.isLoading = true;

            try {
                if (this.isInternalAdmin) {
                    this.startActivitySummaryPolling();
                } else {
                    this.stopActivitySummaryPolling();
                }

                await this.ensureCompanyContext();
                await this.getAllProjects();
            } catch (error) {
                console.error('Fehler beim Aktualisieren des Dashboards: ', error);
                this.isLoading = false;
            }
        },
        setProjectFilterMode(projectFilterMode) {
            if (this.projectFilterMode === projectFilterMode) {
                return;
            }

            this.projectFilterMode = projectFilterMode;
            this.getAllProjects();
        },
        async getAllProjects() {
            this.isLoading = true;
            const loggedInUser = this.loggedInUser;

            try {
                if (!loggedInUser?.id) {
                    this.projects = [];
                    return;
                }

                let response;
                if (this.isInternalAdmin) {
                    if (!this.selectedCompanyId) {
                        this.projects = [];
                        return;
                    }

                    response = await api.get('/api/projekte', {
                        params: {
                            ...this.actorParams(),
                            unternehmen_id: this.selectedCompanyId,
                            ...(this.activeProjectUserFilterId ? { shop_user_id: this.activeProjectUserFilterId } : {}),
                            ...(this.projectFilterMode === 'ms' ? { created_by_internal_admin: 1 } : {}),
                        },
                    });
                } else if (this.isCompanyAdmin) {
                    response = await api.get('/api/projekte', {
                        params: {
                            ...this.actorParams(),
                            unternehmen_id: loggedInUser.unternehmen_id,
                            ...(this.activeProjectUserFilterId ? { shop_user_id: this.activeProjectUserFilterId } : {}),
                        },
                    });
                } else {
                    response = await api.get(`/api/projekte/shop_user/${loggedInUser.id}`, {
                        params: this.actorParams(),
                    });
                }

                this.projects = response.data;
                await this.loadProjectPdfs();

                if (this.isProjectCreated && this.projects.length) {
                    this.isProjectCreated = false;
                    this.configureOrder(this.projects[0]);
                }
            }
            catch (error) {
                console.error('Fehler beim Laden der Projekte: ', error);
            }
            finally {
                this.isLoading = false;
            }
        },
        startActivitySummaryPolling() {
            this.stopActivitySummaryPolling();
            this.loadActivitySummary();
            this.activitySummaryIntervalId = window.setInterval(() => {
                this.loadActivitySummary();
            }, 60000);
        },
        stopActivitySummaryPolling() {
            if (this.activitySummaryIntervalId) {
                window.clearInterval(this.activitySummaryIntervalId);
                this.activitySummaryIntervalId = null;
            }
        },
        async loadActivitySummary() {
            const requestId = ++this.activitySummaryRequestId;
            if (!this.isInternalAdmin || !this.loggedInUser?.id) {
                this.yearlyOrders = null;
                this.yearlyOrdersLoading = false;
                this.yearlyOrdersError = '';
                this.activitySummary = {
                    users_total: 0,
                    active_users_total: 0,
                    online_total: 0,
                    ms_users_online: 0,
                    customer_users_online: 0,
                    online_users: [],
                    orders: {
                        today: { count: 0, prl_count: 0, gross_total: 0 },
                        week: { count: 0, prl_count: 0, gross_total: 0 },
                        month: { count: 0, prl_count: 0, gross_total: 0 },
                    },
                    projects: { red: 0, yellow: 0, green: 0, blue: 0, gray: 0, total: 0 },
                };
                return;
            }

            const companyId = this.activitySummaryCompanyId;
            const year = this.orderSummaryYear;
            if (this.yearlyOrders?.year !== year || this.yearlyOrdersCompanyId !== companyId) {
                this.yearlyOrders = null;
            }
            this.yearlyOrdersLoading = true;
            this.yearlyOrdersError = '';
            try {
                const response = await api.get('/api/shopusers/activity/summary', {
                    params: {
                        ...this.actorParams(),
                        year,
                        ...(companyId ? { unternehmen_id: companyId } : {}),
                    },
                    headers: {
                        'X-Background-Activity': '1',
                    },
                });
                if (requestId !== this.activitySummaryRequestId || companyId !== this.activitySummaryCompanyId || year !== this.orderSummaryYear) {
                    return;
                }
                const yearlyOrders = response.data?.orders_yearly;
                if (Number(yearlyOrders?.year) === year && Array.isArray(yearlyOrders?.months) && yearlyOrders.months.length === 12 && yearlyOrders.total) {
                    this.yearlyOrders = yearlyOrders;
                    this.yearlyOrdersCompanyId = companyId;
                    this.orderSummaryYears = [...new Set([new Date().getFullYear(), year, ...(yearlyOrders.available_years || []).map(Number)])]
                        .sort((a, b) => b - a);
                } else {
                    this.yearlyOrdersError = 'Die Jahresübersicht konnte nicht geladen werden.';
                }
                this.activitySummary = {
                    users_total: Number(response.data?.users_total || 0),
                    active_users_total: Number(response.data?.active_users_total || 0),
                    online_total: Number(response.data?.online_total || 0),
                    ms_users_online: Number(response.data?.ms_users_online || 0),
                    customer_users_online: Number(response.data?.customer_users_online || 0),
                    online_users: Array.isArray(response.data?.online_users)
                        ? response.data.online_users
                        : [],
                    orders: {
                        today: {
                            count: Number(response.data?.orders?.today?.count || 0),
                            prl_count: Number(response.data?.orders?.today?.prl_count || 0),
                            gross_total: Number(response.data?.orders?.today?.gross_total || 0),
                        },
                        week: {
                            count: Number(response.data?.orders?.week?.count || 0),
                            prl_count: Number(response.data?.orders?.week?.prl_count || 0),
                            gross_total: Number(response.data?.orders?.week?.gross_total || 0),
                        },
                        month: {
                            count: Number(response.data?.orders?.month?.count || 0),
                            prl_count: Number(response.data?.orders?.month?.prl_count || 0),
                            gross_total: Number(response.data?.orders?.month?.gross_total || 0),
                        },
                    },
                    projects: {
                        red: Number(response.data?.projects?.red || 0),
                        yellow: Number(response.data?.projects?.yellow || 0),
                        green: Number(response.data?.projects?.green || 0),
                        blue: Number(response.data?.projects?.blue || 0),
                        gray: Number(response.data?.projects?.gray || 0),
                        total: Number(response.data?.projects?.total || 0),
                    },
                };
            } catch (error) {
                if (requestId !== this.activitySummaryRequestId || companyId !== this.activitySummaryCompanyId || year !== this.orderSummaryYear) {
                    return;
                }
                this.yearlyOrdersError = 'Die Jahresübersicht konnte nicht geladen werden.';
                if (error?.response?.status !== 419) {
                    console.error('Fehler beim Laden der Dashboard-Kennzahlen: ', error);
                }
            } finally {
                if (requestId === this.activitySummaryRequestId) {
                    this.yearlyOrdersLoading = false;
                }
            }
        },
        async loadProjectPdfs() {
            this.pdfMap = {};
            const projects = this.projects || [];
            const projectIds = new Set(projects.map(p => p.id));
            if (projectIds.size === 0) {
                return;
            }

            // Nur PDFs der aktuell sichtbaren Projekte laden und danach clientseitig gruppieren.
            try {
                const response = await api.get('/api/projektpdfs', {
                    params: {
                        project_ids: Array.from(projectIds),
                    },
                });
                const list = Array.isArray(response.data) ? response.data : [];

                // Defaults anlegen, damit jeder Eintrag vorhanden ist
                projectIds.forEach(id => {
                    this.pdfMap[id] = { orders: [], requests: [], offers: [] };
                });

                list.forEach(pdf => {
                    const targetId = pdf.projekt_id;
                    if (!projectIds.has(targetId)) return;

                    const bucket = this.pdfMap[targetId] || (this.pdfMap[targetId] = { orders: [], requests: [], offers: [] });
                    if (pdf.type === 1) {
                        bucket.orders.push(pdf);
                    } else if (pdf.type === 2) {
                        bucket.offers.push(pdf);
                    } else if (pdf.type === 0) {
                        bucket.requests.push(pdf);
                    }
                });
            } catch (error) {
                console.error('Fehler beim Laden der PDFs: ', error);
                projectIds.forEach(id => {
                    this.pdfMap[id] = this.pdfMap[id] || { orders: [], requests: [], offers: [] };
                });
            }
        },
        showError(message) {
            this.errorMessage = message;
            this.error = true;
        },
        clearError() {
            this.error = false;
            this.errorMessage = '';
        },
        logout() {
            this.$parent.logoutUser?.();
        },
    },
  }
</script>

<style scoped src="@/styles/DashboardComponent.css">

</style>
